// ** React Imports
import { ReactNode, ReactElement, useRef, useEffect, useState } from 'react';

// ** Hooks Import
import { useAuth } from 'react-oidc-context';

import { RecentHistory } from 'src/layouts/components/panels/recenthistory/RecentHistoryPanel';
import { useSettings } from 'src/@core/hooks/useSettings';

import { ProductFruits } from 'react-product-fruits';
import { User } from 'oidc-client-ts';
import { useAppDispatch } from 'src/store/baseHooks';
import { setAuthData } from 'src/store/slices/authDataSlice';
import { useVigoLogout } from 'src/@core/hooks/useVigoLogout';
import router from 'next/router';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import debug from 'debug';
import { useGetUserProfileQuery } from 'src/queries/services/identity';

const debugLog = debug('auth:AuthGuard');

interface AuthGuardProps {
  children: ReactNode;
  fallback: ReactElement | null;
  hideSpinner: boolean;
}

declare type ProductFruitsUserObject = {
  username: string;
  email?: string;
  role?: string;
  signUpAt?: string;
  firstname?: string;
  lastname?: string;
  props?: UserCustomProps;
  group?: {
    groupId?: string;
    props?: UserGroupCustomProps;
  };
};
declare type UserCustomProps = {
  [key: string]: string | number | Array<string> | Array<number> | UserCustomProps;
};
declare type UserGroupCustomProps = {
  [key: string]: string | number | Array<string> | Array<number> | UserCustomProps;
};


const AuthGuard = (props: AuthGuardProps) => {
  const [oidcStorage, setOidcStorage] = useState<string | null>(null);

  const { children, fallback, hideSpinner } = props;
  const auth = useAuth();
  const { handleLogout } = useVigoLogout();

  const defaultUserInfo: ProductFruitsUserObject = {
    username: '',
    email: '',
    firstname: '',
    lastname: '',
    signUpAt: '',
    role: '',
    props: {}
  };

  const { settings, saveSettings } = useSettings();
  const [pfUserInfo, setPfUserInfo] = useState<ProductFruitsUserObject>(defaultUserInfo);
  const count = useRef<number>(0);
  const RETRYCOUNT = 3;
  // INCREMENT THIS TO FORCE A CACHE CLEAR FOR ALL USERS
  const CACHEVERSION = '9';
  const prod = useGetProductLevel();

  const [errorDuringLogin, setErrorDuringLogin] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  // origin is kept in the render body because it is also used below for the
  // Product Fruits workspace-code selection.
  const origin = typeof window !== 'undefined' && window.location.origin ? window.location.origin : '';

  // Tenant-isolation check: compare the subdomain in the OIDC token against the
  // subdomain the browser is actually on. Calling handleLogout() during render
  // would violate React's rules (side-effect in render body) and could trigger
  // erratic behaviour in Strict Mode. We run it in an effect instead.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const configURL = process.env.NEXT_PUBLIC_DOMAIN as string;
    const currentOrigin = window.location.origin ?? '';
    let loggedInURL = '';

    if (currentOrigin.toLowerCase().includes('localhost')) {
      loggedInURL = configURL;
    } else if (currentOrigin.includes('//')) {
      loggedInURL = currentOrigin.split('//')[1].split('.')[0].split(':')[0];
    }

    if (currentOrigin.includes('.myportal.')) {
      loggedInURL = loggedInURL + '.myportal';
    }

    const oidcStorageStr = sessionStorage.getItem(
      'oidc.user:' + process.env.NEXT_PUBLIC_AUTHORITY + ':' + process.env.NEXT_PUBLIC_CLIENTID
    );

    if (!oidcStorageStr) return;

    let tokenURL = '';
    try {
      const oidcProfile = JSON.parse(oidcStorageStr)?.profile;
      if (oidcProfile) {
        tokenURL = oidcProfile.subdomain ?? '';
        if (oidcProfile.product === 'myportal') {
          tokenURL = tokenURL + '.' + oidcProfile.product;
        }
      }
    } catch {
      // Malformed OIDC storage is suspicious — force logout so the subdomain
      // isolation check cannot be silently skipped.
      debugLog('[AuthGuard] Corrupt OIDC session storage; forcing logout.');
      handleLogout();

      return;
    }

    if (tokenURL === 'www') tokenURL = '';

    if (tokenURL !== '' && loggedInURL.toLowerCase() !== tokenURL.toLowerCase()) {
      handleLogout();
    }
  }, [auth.user, handleLogout]);

  const InitializeRecentHistory = () => {
    let userId = '';
    if (oidcStorage) {
      userId = JSON.parse(oidcStorage).profile.tenantUserId;
    }

    const existingHistory = window.localStorage.getItem(`recent-history-${userId}`);
    if (!existingHistory) {
      window.localStorage.setItem(`recent-history-${userId}`, JSON.stringify([]));
    } else {
      // Delete history entries that are over a day old
      const currentHistory: RecentHistory[] = JSON.parse(existingHistory);
      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);

      const filteredHistory = currentHistory.filter(entry => new Date(entry.expiry) >= yesterdayDate);

      window.localStorage.setItem(`recent-history-${userId}`, JSON.stringify(filteredHistory));
    }
  };

  const handleDumpCache = () => {
    const cacheVersion = window.localStorage.getItem('cacheVersion');
    if (cacheVersion === null || cacheVersion !== CACHEVERSION) {
      debugLog('Clearing cache');
      window.localStorage.clear();
      window.localStorage.setItem('cacheVersion', CACHEVERSION);
    }
  };

  const { data: userData } = useGetUserProfileQuery(auth.user?.profile?.tenantUserId as string);

  useEffect(() => {
    const oidc = sessionStorage.getItem(
      'oidc.user:' + process.env.NEXT_PUBLIC_AUTHORITY + ':' + process.env.NEXT_PUBLIC_CLIENTID
    );

    setOidcStorage(oidc);
  }, [auth]);

  useEffect(() => {
    if (oidcStorage) {
      const user = User.fromStorageString(oidcStorage);
      dispatch(setAuthData(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, oidcStorage]);

  useEffect(() => {
    /**
     * Account configuration
     */
    const user = auth.user;

    if (user !== null && user !== undefined && userData) {
      handleDumpCache();

      // Initialize recent history collection
      InitializeRecentHistory();
      window.sessionStorage.removeItem(`impersonate-${auth.user?.profile.tenantUserId}`);

      const userInfo: ProductFruitsUserObject = {
        username: user?.profile?.tenantUserId as string,
        email: userData?.userProfile?.email ? userData?.userProfile?.email : '',
        firstname: userData?.userProfile?.forname ? userData?.userProfile?.forname : '',
        lastname: userData?.userProfile?.surname ? userData?.userProfile?.surname : '',
        role: userData?.userProfile?.jobTitle ? userData?.userProfile?.jobTitle : '',
        signUpAt: userData?.inviteAccepted ? userData?.inviteAccepted : '',
        props: { Depot: user?.profile.company as string, IsAdmin: user?.profile.defaultRoleIsAdmin as string }
      };

      settings.currentRole = userData?.userProfile?.defaultRoleId as string;
      saveSettings({ ...settings, isImpersonating: false });

      setPfUserInfo(userInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, oidcStorage, userData]);

  /**
   * Handle a token which is expiring.
   * Registered once on mount — auth.events is stable, so no need to re-register
   * on every auth state change. The returned unsubscribe function is the cleanup.
   */
  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      debugLog('Token is expiring');
      auth.signinSilent().catch(error => {
        if (error.message === 'login_required') {
          auth.signinRedirect().catch(err => {
            debugLog('Error during signinRedirect', err);
          });
        } else {
          debugLog('Error during silent signin', error);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle a token which has expired.
   * Same reasoning as above — registered once, cleaned up on unmount.
   */
  useEffect(() => {
    return auth.events.addAccessTokenExpired(() => {
      debugLog('Token has expired');
      auth.signinSilent().catch(error => {
        if (error.message === 'login_required') {
          auth.signinRedirect().catch(err => {
            debugLog('Error during signinRedirect', err);
          });
        } else {
          debugLog('Error during silent signin', error);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle unAuthenticated
   */
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !errorDuringLogin) {
      if (count.current < RETRYCOUNT) {
        count.current = count.current + 1;

        auth.signinRedirect().catch(err => {
          setErrorDuringLogin(true);
          debugLog(err);
        });
      }
    }

    if (errorDuringLogin) {
      router.push('/503');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading, auth.signinRedirect]);

  // try to handle the lack of a back end!

  if (auth.isLoading && !hideSpinner) {
    return fallback;
  }

  // need a better error page
  if (auth.error) {
    debugLog('Auth Error', auth.error);
    count.current = 0;

    return <div>Oops... {auth.error.message}</div>;
  }

  let workspaceCode = '';
  if (prod === Products.Flow && (origin.includes('vigocloud.com') || origin.includes('vigocloud-st.com'))) {
    workspaceCode = 'EnzpCcJ7OngncnlW';
  }
  if (prod === Products.MyPortal && (origin.includes('vigocloud.com') || origin.includes('vigocloud-st.com'))) {
    workspaceCode = '7RLv6CD8AvtOqTe9';
  }

  if (prod === Products.Flow && !(origin.includes('vigocloud.com') || origin.includes('vigocloud-st.com'))) {
    workspaceCode = 'iifPDXKafN0JeiL9';
  }
  if (prod === Products.MyPortal && !(origin.includes('vigocloud.com') || origin.includes('vigocloud-st.com'))) {
    workspaceCode = 'M3sgn4ZLI4aQTd3J';
  }

  if (auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
    count.current = 0;

    return (
      <>
        {pfUserInfo.username != '' && workspaceCode !== '' && (
          <ProductFruits workspaceCode={workspaceCode} language="en" user={pfUserInfo} />
        )}
        <div>{children}</div>
      </>
    );
  }

  return null;
};

export default AuthGuard;
